<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import { formatDate } from "@core/helpers"
import { useResponsive } from "@core/composables"
import {
    barreDecompte,
    largeurSurEchelle,
    lignesArbre,
    piedArbre,
    segmentsDessines,
    DEVENIRS_PLACE,
    sourcesArbre,
    NOTE_ARBRE,
    QUESTIONS_ARBRE,
    type CompositionDecompte,
    type DevenirPlace,
    type FiltresLigne,
    type LigneArbre,
    type OffreDecompte,
} from "@core/composables/useCompanyPlafond"
import { useDevenirSurvole } from "@core/composables/useDevenirSurvole"

/**
 * Tableau du décompte (DEV-621, décision du 14/10, maquette v2 du 16/10).
 *
 * INTENTION. Le tableau dit la même chose que la barre au-dessus de lui :
 * chaque étiquette « Sa place se libère » a la couleur du segment de son
 * devenir, une mini-barre à l'échelle de la grande barre (forfait, ou
 * facturées en dépassement) est posée sous chaque nombre, et le pied reprend
 * la grande barre. Survoler une ligne éclaire son segment et son entrée de
 * légende ; survoler la barre éclaire les lignes du même devenir.
 */

// ================================
// TYPES & INTERFACES
// ================================
type Props = {
    /**
     * Composition servie par la route de comptage (`plafond.composition`) :
     * les cinq feuilles et les deux sous-feuilles des sources ajoutées avant
     * la période
     */
    composition: CompositionDecompte
    /** Sources facturées servies (`plafond.facturees`) */
    facturees: number
    /** Places du forfait (`plafond.forfait`) */
    forfait: number
    /** Offre dont la règle s'applique : EM n'a pas de question 3 */
    offre: OffreDecompte
    /** Début de la période en cours */
    debut?: string | Date | null
    /** Fin de la période en cours */
    fin?: string | Date | null
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<Props>(), { debut: null, fin: null })

const emit = defineEmits<{
    /** Ouvre la liste de facturation filtrée sur les sources d'une ligne */
    "ouvrir-liste": [filtres: FiltresLigne]
}>()

const { isMobile } = useResponsive()

const { survoler, quitter, entrerPointeur, sortirPointeur, estEclaire } =
    useDevenirSurvole()

// ================================
// COMPUTED
// ================================

/** Lignes du tableau de synthèse (cellules fusionnées par `rowspan`) */
const getLignes = computed(() =>
    lignesArbre(props.composition, props.offre, props.debut, props.fin)
)

/** Grande barre de la carte : son échelle et ses segments dessinés */
const getBarre = computed(() =>
    barreDecompte(
        props.composition,
        props.offre,
        props.forfait,
        props.facturees,
        props.fin
    )
)

/** Échelle des mini-barres : celle de la grande barre */
const getEchelle = computed(() => getBarre.value?.max ?? 0)

/** Mini-barre du pied : les segments dessinés de la grande barre */
const getSegmentsPied = computed(() => segmentsDessines(getBarre.value))

/** Vrai : question 3 présente (Energy Analytics) */
const getAvecQ3 = computed(() => props.offre === "EA")

/**
 * Colonnes du libellé du pied : les questions, « Facturée sur la période ? »
 * et « Sa place se libère » ; le total va dans la colonne Sources
 */
const getColonnesPied = computed(() => (getAvecQ3.value ? 5 : 4))

/** Entrée de la liste mobile : une par ligne du tableau */
type EntreeMobile = {
    ligne: LigneArbre
    /** « Sur votre compte, ajoutées avant le 30/09 » */
    libelle: string
    /** « Facturées : Oui. Place libérée dès la suppression. » */
    detail: string
}

/**
 * Liste mobile (au lieu du tableau coupé) : le chemin de chaque ligne dans
 * l'arbre en clair, son nombre, puis sa feuille et sa place
 */
const getEntreesMobile = computed((): EntreeMobile[] => {
    const d = formatDate(props.debut, "DD/MM", "")
    let q1 = ""
    let q2 = ""
    return getLignes.value.map((ligne) => {
        if (ligne.q1) {
            q1 =
                ligne.q1.texte === "Oui"
                    ? "Sur votre compte"
                    : "Supprimées pendant la période"
        }
        if (ligne.q2) {
            const pendant = ligne.q2.texte.startsWith("Oui")
            q2 = pendant
                ? d
                    ? `ajoutées depuis le ${d}`
                    : "ajoutées pendant la période"
                : d
                  ? `ajoutées avant le ${d}`
                  : "ajoutées avant la période"
        }
        const q3 = ligne.q3
            ? ligne.q3.texte === "Oui"
                ? "mises à jour pendant la période"
                : "non mises à jour pendant la période"
            : null
        const place = ligne.place.startsWith("libérée")
            ? ligne.place
            : `libérée ${ligne.place}`
        return {
            ligne,
            libelle: [q1, q2, q3].filter(Boolean).join(", "),
            detail: `Facturées : ${ligne.feuille}. Place ${place}.`,
        }
    })
})

/**
 * Mini-barre d'une ligne : largeur à l'échelle de la grande barre, couleur
 * du devenir (surface-300 pour les sources non facturées) ; vide à 0 ou non
 * mesuré
 * @param ligne - ligne du tableau
 */
function miniBarre(ligne: LigneArbre) {
    return {
        width: largeurSurEchelle(ligne.sources, getEchelle.value),
        backgroundColor: DEVENIRS_PLACE[ligne.devenir].couleur,
    }
}

/**
 * Étiquette « Sa place se libère » : couleur du devenir
 * @param devenir - devenir de la ligne
 */
function etiquette(devenir: DevenirPlace) {
    const d = DEVENIRS_PLACE[devenir]
    return { severity: d.severite, class: d.classeEtiquette }
}

/** Nombre cliquable : servi, non nul et retrouvé par la liste (jamais un 0) */
const estLien = (ligne: LigneArbre): boolean =>
    !!ligne.sources && !!ligne.filtres
</script>

<template>
    <div class="company-subscription-decompte-tree flex flex-col gap-2">
        <!-- Mobile : une liste, une entrée par ligne du tableau -->
        <div
            v-if="isMobile"
            class="company-subscription-decompte-tree__mobile flex flex-col gap-2"
        >
            <ul class="flex flex-col gap-2">
                <li
                    v-for="(entree, index) in getEntreesMobile"
                    :key="index"
                    class="company-subscription-decompte-tree__entree border-b border-surface-200 pb-2 transition-colors motion-reduce:transition-none"
                    :class="{
                        'company-subscription-decompte-tree__entree--eclairee bg-primary-50':
                            estEclaire(entree.ligne.devenir),
                    }"
                    :data-devenir="entree.ligne.devenir"
                    @pointerenter="entrerPointeur($event, entree.ligne.devenir)"
                    @pointerleave="sortirPointeur"
                    @focusin="survoler(entree.ligne.devenir)"
                    @focusout="quitter"
                >
                    <p class="font-medium text-surface-800">
                        {{ entree.libelle }} :
                        <button
                            v-if="estLien(entree.ligne)"
                            type="button"
                            class="company-subscription-decompte-tree__lien font-medium text-primary-700 underline underline-offset-2 tabular-nums"
                            @click="emit('ouvrir-liste', entree.ligne.filtres!)"
                        >
                            {{ sourcesArbre(entree.ligne.sources) }}
                        </button>
                        <span
                            v-else
                            class="tabular-nums"
                            :class="
                                entree.ligne.sources === null
                                    ? 'italic text-surface-500'
                                    : 'text-surface-500'
                            "
                        >
                            {{ sourcesArbre(entree.ligne.sources) }}
                        </span>
                    </p>
                    <span
                        class="company-subscription-decompte-tree__mini mt-1.5 block h-1.5 overflow-hidden rounded-full bg-surface-100"
                        aria-hidden="true"
                    >
                        <span
                            class="company-subscription-decompte-tree__mini-part block h-full"
                            :style="miniBarre(entree.ligne)"
                        />
                    </span>
                    <p class="text-sm text-surface-600">
                        {{ entree.detail }}
                    </p>
                </li>
            </ul>
            <div
                class="company-subscription-decompte-tree__pied-mobile font-semibold text-surface-900"
            >
                <p>
                    Facturées sur la période : {{ facturees }} sur
                    {{ forfait }}
                </p>
                <span
                    class="company-subscription-decompte-tree__mini company-subscription-decompte-tree__mini--pied mt-1.5 flex h-1.5 overflow-hidden rounded-full bg-surface-100"
                    aria-hidden="true"
                >
                    <span
                        v-for="segment in getSegmentsPied"
                        :key="segment.id"
                        class="company-subscription-decompte-tree__mini-part block h-full"
                        :data-devenir="segment.devenir"
                        :style="{
                            width: largeurSurEchelle(
                                segment.etendue,
                                getEchelle
                            ),
                            backgroundColor: segment.couleur,
                        }"
                    />
                </span>
            </div>
            <p
                v-if="getAvecQ3"
                class="company-subscription-decompte-tree__note text-sm text-surface-600"
            >
                {{ NOTE_ARBRE }}
            </p>
        </div>
        <div v-else class="overflow-x-auto">
            <table
                class="w-full min-w-[40rem] border-collapse text-sm tabular-nums"
            >
                <thead>
                    <tr class="bg-surface-50">
                        <th
                            class="border border-surface-200 px-3 py-2 text-left text-sm font-medium text-surface-800"
                        >
                            {{ QUESTIONS_ARBRE.q1 }}
                        </th>
                        <th
                            class="border border-surface-200 px-3 py-2 text-left text-sm font-medium text-surface-800"
                        >
                            {{ QUESTIONS_ARBRE.q2 }}
                        </th>
                        <th
                            v-if="getAvecQ3"
                            class="border border-surface-200 px-3 py-2 text-left text-sm font-medium text-surface-800"
                        >
                            <span
                                class="company-subscription-decompte-tree__q3 inline-flex items-center gap-1"
                            >
                                {{ QUESTIONS_ARBRE.q3 }}
                                <BaseTooltipMessage
                                    :content="NOTE_ARBRE"
                                    title="Mise à jour pendant la période"
                                    severity="secondary"
                                    size="small"
                                    position="top"
                                />
                            </span>
                        </th>
                        <th
                            class="border border-surface-200 px-3 py-2 text-left text-sm font-medium text-surface-800"
                        >
                            {{ QUESTIONS_ARBRE.feuille }}
                        </th>
                        <th
                            class="border border-surface-200 px-3 py-2 text-left text-sm font-medium text-surface-800"
                        >
                            {{ QUESTIONS_ARBRE.place }}
                        </th>
                        <th
                            class="border border-surface-200 px-3 py-2 text-right text-sm font-medium text-surface-800"
                        >
                            {{ QUESTIONS_ARBRE.sources }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="(ligne, index) in getLignes"
                        :key="index"
                        class="company-subscription-decompte-tree__ligne transition-colors motion-reduce:transition-none"
                        :class="{
                            'company-subscription-decompte-tree__ligne--eclairee bg-primary-50':
                                estEclaire(ligne.devenir),
                        }"
                        :data-devenir="ligne.devenir"
                        @pointerenter="entrerPointeur($event, ligne.devenir)"
                        @pointerleave="sortirPointeur"
                        @focusin="survoler(ligne.devenir)"
                        @focusout="quitter"
                    >
                        <td
                            v-if="ligne.q1"
                            :rowspan="ligne.q1.rowspan"
                            class="border border-surface-200 px-3 py-2 font-medium text-surface-800"
                        >
                            {{ ligne.q1.texte }}
                        </td>
                        <td
                            v-if="ligne.q2"
                            :rowspan="ligne.q2.rowspan"
                            :colspan="
                                getAvecQ3 && ligne.q2.colspan
                                    ? ligne.q2.colspan
                                    : undefined
                            "
                            class="border border-surface-200 px-3 py-2 text-surface-700"
                        >
                            {{ ligne.q2.texte }}
                            <span
                                v-if="ligne.q2.total !== undefined"
                                class="company-subscription-decompte-tree__total-groupe block text-xs text-surface-500"
                            >
                                {{ ligne.q2.total }}
                                {{
                                    ligne.q2.total > 1
                                        ? "sources au total"
                                        : "source au total"
                                }}
                            </span>
                        </td>
                        <td
                            v-if="getAvecQ3 && ligne.q3"
                            class="border border-surface-200 px-3 py-2 text-surface-700"
                        >
                            {{ ligne.q3.texte }}
                        </td>
                        <td
                            class="company-subscription-decompte-tree__feuille border border-surface-200 px-3 py-2 font-medium text-surface-800"
                        >
                            {{ ligne.feuille }}
                        </td>
                        <td
                            class="company-subscription-decompte-tree__place border border-surface-200 px-3 py-2 text-surface-700"
                        >
                            <BaseTag
                                :value="ligne.place"
                                v-bind="etiquette(ligne.devenir)"
                                :data-devenir="ligne.devenir"
                            />
                        </td>
                        <td
                            class="company-subscription-decompte-tree__sources border border-surface-200 px-3 py-2 text-right text-surface-700"
                            :class="{
                                'italic text-surface-500':
                                    ligne.sources === null,
                                'text-surface-500': ligne.sources === 0,
                            }"
                        >
                            <button
                                v-if="estLien(ligne)"
                                type="button"
                                class="company-subscription-decompte-tree__lien font-medium text-primary-700 underline underline-offset-2"
                                @click="emit('ouvrir-liste', ligne.filtres!)"
                            >
                                {{ sourcesArbre(ligne.sources) }}
                            </button>
                            <template v-else>
                                {{ sourcesArbre(ligne.sources) }}
                            </template>
                            <span
                                class="company-subscription-decompte-tree__mini mt-1.5 block h-1.5 overflow-hidden rounded-full bg-surface-100"
                                aria-hidden="true"
                            >
                                <span
                                    class="company-subscription-decompte-tree__mini-part block h-full"
                                    :style="miniBarre(ligne)"
                                />
                            </span>
                        </td>
                    </tr>
                </tbody>
                <tfoot>
                    <tr class="bg-surface-50 font-semibold text-surface-900">
                        <th
                            :colspan="getColonnesPied"
                            class="company-subscription-decompte-tree__pied border border-surface-200 px-3 py-2 text-left"
                        >
                            {{ piedArbre(forfait) }}
                        </th>
                        <th
                            class="company-subscription-decompte-tree__total border border-surface-200 px-3 py-2 text-right"
                        >
                            {{ facturees }}
                            <span
                                class="company-subscription-decompte-tree__mini company-subscription-decompte-tree__mini--pied mt-1.5 flex h-1.5 overflow-hidden rounded-full bg-surface-100"
                                aria-hidden="true"
                            >
                                <span
                                    v-for="segment in getSegmentsPied"
                                    :key="segment.id"
                                    class="company-subscription-decompte-tree__mini-part block h-full"
                                    :data-devenir="segment.devenir"
                                    :style="{
                                        width: largeurSurEchelle(
                                            segment.etendue,
                                            getEchelle
                                        ),
                                        backgroundColor: segment.couleur,
                                    }"
                                />
                            </span>
                        </th>
                    </tr>
                </tfoot>
            </table>
        </div>
    </div>
</template>
